import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { submittalsApi, type Submittal } from '@/lib/apiClient';
import { Modal, Button, SelectWithOther } from '@/components';
import ContactOrRoleSelect from './contacts/ContactOrRoleSelect';
import { useJobContacts } from './contacts/jobContacts';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import {
  SUBMITTAL_TYPE_OPTIONS, SUBMITTAL_REVIEWER_OPTIONS, SUBMITTAL_FIELD_MAX,
  formFromSubmittal, buildEditPatch, editProblem, submittalWithNumber,
  type SubmittalEditForm,
} from './submittalEdit';

/**
 * Edit a submittal's own details: number, title, spec section, type, reviewer and
 * received date. Everything the review workflow owns (status, decision, ball in
 * court, review due and window) keeps its own control on the detail screen.
 *
 * Styled with the `sub-*` classes SubmittalsTab declares. This only ever renders
 * from that tab's detail view, so they are always mounted alongside it.
 */
export default function SubmittalEditModal({
  sub,
  onClose,
  onSaved,
}: {
  sub: Submittal;
  onClose: () => void;
  /** After a successful save — the detail view refreshes the record, list and history. */
  onSaved: () => void;
}) {
  const [form, setForm] = useState<SubmittalEditForm>(() => formFromSubmittal(sub));
  // The reviewer as a job contact (#598 step 2), beside the text fields the patch helper
  // compares: the contact picked, or null for a label.
  const [reviewerContactId, setReviewerContactId] = useState<number | null>(sub.reviewerContact?.id ?? null);
  const { options: jobContacts, isLoading: contactsLoading } = useJobContacts(sub.projectId);
  const contactChanged = reviewerContactId !== (sub.reviewerContact?.id ?? null);

  const textPatch = buildEditPatch(sub, form);
  // A picked contact's name is the server's to write: it can outrun the label's 80
  // characters, which the API would refuse.
  const patch: Record<string, unknown> = contactChanged || reviewerContactId != null
    ? (() => {
      const { reviewer: _label, ...rest } = textPatch;
      return reviewerContactId != null
        ? (contactChanged ? { ...rest, reviewerContactId } : rest)
        : { ...textPatch, reviewer: form.reviewer.trim(), reviewerContactId: null };
    })()
    : textPatch;
  const dirty = Object.keys(patch).length > 0;
  const problem = editProblem(sub, form);

  // A picker sitting on "Other…" with its box still empty reads, to the form, as a
  // CLEARED field — and would be saved as one. Hold Save until it is filled in or
  // another entry is picked. Clearing on purpose is the blank entry in the list.
  const [otherOpen, setOtherOpen] = useState({ submittalType: false, reviewer: false });
  const watchOther = (k: 'submittalType' | 'reviewer') => (pending: boolean) =>
    setOtherOpen((p) => (p[k] === pending ? p : { ...p, [k]: pending }));
  const unfinished = otherOpen.submittalType ? 'type' : otherOpen.reviewer ? 'reviewer' : null;

  // The job's other submittals, only to warn about a repeated number — so only
  // fetched once the number has been edited, not on every open. Same key and
  // fetcher as the list, so it is often already cached.
  const siblings = useQuery({
    queryKey: ['submittals', sub.projectId],
    queryFn: () => submittalsApi.list({ projectId: sub.projectId }),
    enabled: textPatch.submittalNumber !== undefined,
  });
  const repeat = textPatch.submittalNumber !== undefined
    ? submittalWithNumber(siblings.data ?? [], sub.id, textPatch.submittalNumber)
    : undefined;

  const save = useMutation({
    mutationFn: () => submittalsApi.update(sub.id, patch),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  // Cancel, the ✕, Escape and a click outside all close through here, so typed
  // changes are never thrown away without asking. A successful save closes via
  // onClose directly — there is nothing left to discard.
  //
  // Not while a save is in flight: if it then FAILED, the error would have nowhere
  // to show and everything typed would be gone, with Cancel greyed out to say
  // closing was off the table.
  const dismiss = () => {
    if (save.isPending) return;
    if (dirty && !window.confirm('Discard your changes to this submittal?')) return;
    onClose();
  };

  // A failed save's message describes the values that were sent. Once the user changes
  // anything it no longer does, so it goes rather than sitting beside the new value.
  const edited = () => { if (save.isError) save.reset(); };
  const text = (k: keyof SubmittalEditForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => { edited(); setForm((p) => ({ ...p, [k]: e.target.value })); };
  const pick = (k: keyof SubmittalEditForm) => (v: string) => { edited(); setForm((p) => ({ ...p, [k]: v })); };

  return (
    <Modal
      isOpen
      onClose={dismiss}
      title="Edit submittal"
      // With unsaved changes a click outside does nothing, rather than asking: a text
      // selection dragged out of the dialog ends on the backdrop as a click, and that
      // should neither discard the edit nor interrupt it with a prompt. Cancel, the ✕
      // and Escape are deliberate, and those go through dismiss.
      closeOnBackdropClick={!dirty}
      footer={
        <>
          <Button variant="secondary" onClick={dismiss} disabled={save.isPending}>Cancel</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || problem !== null || unfinished !== null || save.isPending}
            isLoading={save.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      {save.isError && (
        <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
          {(save.error as Error).message}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
        <div className="sub-grid2">
          <div className="sub-field">
            <label className="sub-label" htmlFor="sub-edit-number">Submittal #</label>
            <input id="sub-edit-number" className="sub-input" value={form.submittalNumber} onChange={text('submittalNumber')} placeholder="e.g. SUB-001" maxLength={SUBMITTAL_FIELD_MAX.submittalNumber} />
            {repeat ? (
              <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)', color: '#92400E' }}>
                Another submittal on this job already uses {repeat.submittalNumber}.
              </p>
            ) : (
              <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>
                Used in the file name of future uploads. Files already uploaded keep their names.
              </p>
            )}
          </div>
          <div className="sub-field">
            <label className="sub-label" htmlFor="sub-edit-title">Title</label>
            <input id="sub-edit-title" className="sub-input" value={form.title} onChange={text('title')} maxLength={SUBMITTAL_FIELD_MAX.title} />
          </div>
        </div>
        <div className="sub-grid2">
          <div className="sub-field">
            <label className="sub-label" htmlFor="sub-edit-spec">Spec section</label>
            <input id="sub-edit-spec" className="sub-input" value={form.specSection} onChange={text('specSection')} placeholder="e.g. 03 30 00" maxLength={SUBMITTAL_FIELD_MAX.specSection} />
          </div>
          <SelectWithOther
            label="Type"
            options={SUBMITTAL_TYPE_OPTIONS}
            value={form.submittalType}
            onChange={pick('submittalType')}
            onPendingChange={watchOther('submittalType')}
            placeholder="Select type…"
          />
        </div>
        <ContactOrRoleSelect
          label="Reviewer"
          contacts={jobContacts}
          loading={contactsLoading}
          roles={SUBMITTAL_REVIEWER_OPTIONS}
          value={{ contactId: reviewerContactId, label: form.reviewer }}
          current={sub.reviewerContact}
          onChange={(v) => { edited(); setReviewerContactId(v.contactId); setForm((p) => ({ ...p, reviewer: v.label })); }}
          onPendingChange={watchOther('reviewer')}
          placeholder="Select reviewer…"
        />
        <div className="sub-grid2">
          <div className="sub-field">
            <label className="sub-label" htmlFor="sub-edit-received">Received</label>
            <input
              id="sub-edit-received"
              className="sub-input"
              type="date"
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              value={form.receivedAt}
              onChange={text('receivedAt')}
            />
          </div>
        </div>
        {problem && dirty ? (
          <p className="sub-help" style={{ color: 'var(--ew-danger)' }}>{problem}</p>
        ) : unfinished ? (
          <p className="sub-help">Enter the {unfinished} in the box, or pick one from the list.</p>
        ) : null}
        <p className="sub-help">
          Status, decision, ball in court and the review window have their own controls on the submittal.
        </p>
      </div>
    </Modal>
  );
}
